import { RiShareForwardLine } from "react-icons/ri";
import { BiHeart } from "react-icons/bi";
import React, { useState, useEffect } from "react";
import Layout from "../components/Layout/Layout";
import { useNavigate, useParams } from "react-router-dom";
import { useCart } from "../context/cart";
import { useAuth } from "../context/auth";
import toast from "react-hot-toast";
import { FaStar, FaCheckCircle, FaMinus, FaPlus } from "react-icons/fa";
import { FiAlertTriangle } from "react-icons/fi";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";
import { TbTruckDelivery } from "react-icons/tb";
import { MdOutlineKeyboardReturn, MdVerified } from "react-icons/md";
import { HiOutlineVideoCamera } from "react-icons/hi";
import { BsShieldCheck } from "react-icons/bs";
import ProductCard from "./ProductCard";
import axios from "axios";
import { getImageUrl } from "../utils/imageHelper";
import api from "../utils/axiosInstance";

const PALETTE = {
  DARK_NAVY: "#1C2E4A",
  ACCENT_BROWN: "#7C4F2B",
  SECONDARY_WHITE: "#EBEFF5",
};

const ProductDetails = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [cart, setCart] = useCart();
  const [auth] = useAuth();

  const [product, setProduct] = useState({});
  const [relatedProducts, setRelatedProducts] = useState([]);

  const [wishlist, setWishlist] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [wishlistLoaded, setWishlistLoaded] = useState(false);
  const [selectedColor, setSelectedColor] = useState("");
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewFilter, setReviewFilter] = useState("all");
  const [selectedSize, setSelectedSize] = useState("");
  const sizes = ["XS", "S", "M", "L", "XL", "XXL", "XXXL"];
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [showPolicies, setShowPolicies] = useState(false);
  const [activeTab, setActiveTab] = useState("description");
  const [quantity, setQuantity] = useState(1);

  const ratingDescriptions = {
    1: { text: "Poor", color: "text-red-500" },
    2: { text: "Fair", color: "text-orange-500" },
    3: { text: "Good", color: "text-yellow-600" },
    4: { text: "Very Good", color: "text-green-500" },
    5: { text: "Excellent", color: "text-green-600" },
  };

  const currentRating = hover || rating;
  const currentDescription = ratingDescriptions[currentRating];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (params?.slug) {
      getProduct();
      if (auth?.token) getWishlistItems();
    }
  }, [params?.slug, auth?.token]);

  const authHeaders = {
    headers: { Authorization: `Bearer ${auth?.token}` },
  };

  const getProduct = async () => {
    try {
      const { data } = await axios.get(
        `/api/v1/product/get-product/${params.slug}`,
      );
      setProduct(data?.product);
      getSimilarProduct(data?.product._id, data?.product.category._id);
      fetchReviews(data?.product._id);
    } catch (error) {
      console.error(error);
    }
  };

  const getSimilarProduct = async (pid, cid) => {
    try {
      const { data } = await axios.get(
        `/api/v1/product/related-product/${pid}/${cid}`,
      );
      setRelatedProducts(data?.products || []);
    } catch (error) {
      console.error(error);
    }
  };

const handleAddToCart = async (product) => {
  if (!selectedSize) {
    toast.error("Please select a size");
    return;
  }
  if (!selectedColor) {
    toast.error("Please select a color");
    return;
  }

  try {
    await api.post(`/api/v1/cart/${product._id}`, {
      qty: quantity,
      size: selectedSize,
      color: selectedColor,
    });

    const cartItem = {
      ...product,
      quantity,
      size: selectedSize,
      color: selectedColor,
    };

    const updatedCart = [...cart, cartItem];
    setCart(updatedCart);
    localStorage.setItem("cart", JSON.stringify(updatedCart));

    toast.success("Added to cart!");
  } catch (err) {
    console.error(err);
    toast.error("Something went wrong");
  }
};

  const handleBuyNow = async (product) => {
    if (!selectedSize) {
      toast.error("Please select a size");
      return;
    }

    if (!selectedColor) {
      toast.error("Please select a color");
      return;
    }

    try {
      await api.post(`/api/v1/cart/${product._id}`, {
        qty: quantity,
        size: selectedSize,
        color: selectedColor,
      });

      const cartItem = {
        ...product,
        quantity,
        size: selectedSize,
        color: selectedColor,
      };

      const updatedCart = [...cart, cartItem];

      setCart(updatedCart);
      localStorage.setItem("cart", JSON.stringify(updatedCart));

      toast.success("Redirecting to cart...");
      navigate("/cart");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    }
  };

  const getWishlistItems = async () => {
    try {
      const { data } = await axios.get("/api/v1/wishlist", authHeaders);
      if (data?.wishlist && Array.isArray(data.wishlist)) {
        const wishlistIds = data.wishlist.map((item) => item._id.toString());
        setWishlist(wishlistIds);
      } else {
        setWishlist([]);
      }
    } catch (error) {
      console.error("Error fetching wishlist:", error.response?.data || error);
      setWishlist([]);
    } finally {
      setWishlistLoaded(true);
    }
  };

  const toggleWishlist = async () => {
    if (!auth?.token) {
      toast.error("Please login to manage your wishlist");
      return;
    }

    const productId = product._id?.toString();
    const alreadyInWishlist = wishlist.includes(productId);

    try {
      setWishlistLoading(true);

      if (alreadyInWishlist) {
        await axios.delete(`/api/v1/wishlist/${product._id}`, authHeaders);
        setWishlist(wishlist.filter((id) => id !== productId));
        toast.success("Removed from Wishlist");
      } else {
        await axios.post(`/api/v1/wishlist/${product._id}`, {}, authHeaders);
        setWishlist([...wishlist, productId]);
        toast.success("Added to Wishlist!");
      }
    } catch (error) {
      toast.error("Wishlist update failed");
      console.error(error.response?.data || error);
    } finally {
      setWishlistLoading(false);
    }
  };

  const fetchReviews = async (productId) => {
    try {
      setLoadingReviews(true);
      const { data } = await axios.get(`/api/v1/reviews/product/${productId}`);
      setReviews(data?.reviews || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingReviews(false);
    }
  };

  const submitReview = async () => {
    if (!auth?.token) {
      toast.error("Please login to write a review");
      return;
    }

    if (!rating) {
      toast.error("Please select a rating");
      return;
    }

    if (!comment.trim() || comment.trim().length < 10) {
      toast.error("Please write at least 10 characters");
      return;
    }

    try {
      setSubmitting(true);
      await axios.post(`/api/v1/reviews/${product._id}`, {
        rating,
        comment,
      });
      toast.success("Review submitted successfully!");

      setRating(0);
      setHover(0);
      setComment("");
      setShowReviewForm(false);

      fetchReviews(product._id);
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to submit review";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const shareProduct = () => {
    if (navigator.share) {
      navigator.share({
        title: product.name,
        text: product.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Product link copied!");
    }
  };

  const calculateRatingStats = () => {
    if (reviews.length === 0) return { average: 0, distribution: {} };

    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    let total = 0;

    reviews.forEach((review) => {
      distribution[review.rating]++;
      total += review.rating;
    });

    return {
      average: (total / reviews.length).toFixed(1),
      distribution,
      total: reviews.length,
    };
  };

  const stats = calculateRatingStats();

  const getFilteredReviews = () => {
    let filtered = [...reviews];

    if (reviewFilter !== "all") {
      filtered = filtered.filter((r) => r.rating === parseInt(reviewFilter));
    }

    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return filtered;
  };

  const filteredReviews = getFilteredReviews();

  return (
    <Layout>
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12 mt-16 sm:mt-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            <div className="lg:sticky lg:top-24 lg:self-start">
              <div className="relative bg-gray-50 rounded-2xl overflow-hidden border border-gray-200">
                <img
                  src={getImageUrl(product.photos?.[mainImageIndex])}
                  alt={product.name}
                  className="w-full h-[350px] sm:h-[450px] lg:h-[550px] object-contain p-6"
                />

                <div className="absolute top-4 right-4 flex gap-2">
                  <button
                    onClick={toggleWishlist}
                    disabled={wishlistLoading || !wishlistLoaded}
                    className="w-11 h-11 rounded-full bg-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center disabled:opacity-50 border border-gray-100"
                  >
                    <BiHeart
                      size={22}
                      className={`transition ${
                        wishlist.includes(product._id?.toString())
                          ? "text-red-500 fill-current"
                          : "text-gray-700"
                      }`}
                    />
                  </button>

                  <button
                    onClick={shareProduct}
                    className="w-11 h-11 rounded-full bg-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center border border-gray-100"
                  >
                    <RiShareForwardLine size={22} className="text-gray-700" />
                  </button>
                </div>
              </div>

              <div className="mt-4 flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                {product.photos?.map((photo, i) => (
                  <button
                    key={i}
                    onClick={() => setMainImageIndex(i)}
                    className={`flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden border-2 transition-all ${
                      mainImageIndex === i
                        ? "border-black ring-2 ring-black ring-offset-2"
                        : "border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    <img
                      src={getImageUrl(photo)}
                      alt=""
                      className="object-cover w-full h-full"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <p className="text-xs sm:text-sm uppercase tracking-wider text-gray-500 font-medium mb-2">
                  {product?.category?.name}
                </p>
                <h1
                  className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-tight"
                  style={{ color: PALETTE.DARK_NAVY }}
                >
                  {product.name}
                </h1>
              </div>

              {stats.total > 0 && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <FaStar
                        key={i}
                        size={18}
                        className={
                          i < Math.round(stats.average)
                            ? "text-yellow-400"
                            : "text-gray-300"
                        }
                      />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {stats.average}
                  </span>
                  <span className="text-sm text-gray-500">
                    ({stats.total} reviews)
                  </span>
                </div>
              )}
              {/* Price */}
              <div className="space-y-3">
                <div className="flex items-center gap-4 flex-wrap">
                  {/* Selling Price */}
                  <span
                    className="text-4xl font-bold"
                    style={{ color: PALETTE.ACCENT_BROWN }}
                  >
                    ₹{Number(product.price || 0).toLocaleString("en-IN")}
                  </span>

                  {/* MRP */}
                  {product.actualPrice &&
                    Number(product.actualPrice) > Number(product.price) && (
                      <span className="text-2xl text-gray-400 line-through">
                        ₹{Number(product.actualPrice).toLocaleString("en-IN")}
                      </span>
                    )}
                </div>

                {/* Discount */}
                {product.actualPrice &&
                  Number(product.actualPrice) > Number(product.price) && (
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700">
                        {Math.round(
                          ((Number(product.actualPrice) -
                            Number(product.price)) /
                            Number(product.actualPrice)) *
                            100,
                        )}
                        % OFF
                      </span>

                      <span className="text-sm text-gray-500">
                        Save ₹
                        {(
                          Number(product.actualPrice) - Number(product.price)
                        ).toLocaleString("en-IN")}
                      </span>
                    </div>
                  )}

                {product.stock === 0 ? (
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-red-600 text-sm font-medium">
                    ✕ Out of Stock
                  </div>
                ) : product.stock <= 5 ? (
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-sm font-medium">
                    <FiAlertTriangle className="text-amber-600" />
                    Only {product.stock} left
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 text-green-700 text-sm font-medium">
                    <FaCheckCircle />
                    In Stock
                  </div>
                )}
              </div>

              {/* Size */}
              <div className="space-y-3 pt-6 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-800">
                    Select Size
                  </h3>

                  <button className="text-sm text-gray-500 hover:text-black underline">
                    Size Guide
                  </button>
                </div>

                <div className="flex flex-wrap gap-3">
                  {product.sizes?.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`min-w-[52px] h-12 rounded-lg border text-sm font-semibold transition-all duration-200
        ${
          selectedSize === size
            ? "bg-black text-white border-black shadow-lg scale-105"
            : "bg-white border-gray-300 hover:border-black hover:shadow"
        }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>

                {selectedSize && (
                  <p className="text-sm text-green-600 font-medium">
                    Selected Size: {selectedSize}
                  </p>
                )}
              </div>
              <div className="space-y-3 pt-6 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-800">
                    Select Color
                  </h3>

                  {selectedColor && (
                    <span className="text-sm text-gray-500">
                      {selectedColor.name}
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-4">
                  {product.colors?.map((color, index) => {
                    const isSelected =
                      selectedColor?.name === color.name &&
                      selectedColor?.hex === color.hex;

                    return (
                      <button
                        key={color._id || `${color.name}-${index}`}
                        type="button"
                        onClick={() => setSelectedColor(color)}
                        className="flex flex-col items-center gap-2 group"
                      >
                        <div
                          className={`relative flex items-center justify-center w-12 h-12 rounded-full transition-all duration-200 ${
                            isSelected
                              ? "ring-2 ring-black ring-offset-2"
                              : "ring-1 ring-gray-300 group-hover:ring-gray-500"
                          }`}
                        >
                          <div
                            className="w-9 h-9 rounded-full"
                            style={{
                              backgroundColor: color.hex,
                              border:
                                color.hex?.toLowerCase() === "#ffffff"
                                  ? "1px solid #d1d5db"
                                  : "none",
                            }}
                          />

                          {isSelected && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-black text-white flex items-center justify-center text-[10px] shadow">
                              ✓
                            </div>
                          )}
                        </div>

                        <span
                          className={`text-xs font-medium transition-colors ${
                            isSelected ? "text-black" : "text-gray-500"
                          }`}
                        >
                          {color.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Quantity */}
              <div className="space-y-3 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-800">
                  Quantity
                </h3>

                <div className="inline-flex items-center rounded-lg border border-gray-300 bg-white overflow-hidden shadow-sm">
                  <button
                    onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                    className="w-10 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-black transition-all"
                  >
                    <FaMinus size={11} />
                  </button>

                  <div className="w-12 h-9 border-x border-gray-300 flex items-center justify-center text-sm font-semibold text-gray-900">
                    {quantity}
                  </div>

                  <button
                    onClick={() =>
                      setQuantity((prev) =>
                        Math.min(product.stock || 99, prev + 1),
                      )
                    }
                    className="w-10 h-9 flex items-center justify-center text-gray-600 hover:bg-gray-100 hover:text-black transition-all"
                  >
                    <FaPlus size={11} />
                  </button>
                </div>

                <p className="text-xs text-gray-500">
                  Maximum available: {product.stock}
                </p>
              </div>

              <div className="border-t border-b border-gray-200 py-5">
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                  {product.description}
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => handleAddToCart(product)}
                    disabled={product.stock === 0}
                    className="flex-1 px-8 py-4 border-2 border-black text-black rounded-lg text-base font-medium tracking-wide hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add to Cart
                  </button>
                  <button
                    onClick={() => handleBuyNow(product)}
                    disabled={product.stock === 0}
                    className="flex-1 px-8 py-4 bg-black text-white rounded-lg text-base font-medium tracking-wide hover:bg-gray-900 transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Buy Now
                  </button>
                </div>
              </div>

              <div className="lg:hidden">
                <button
                  onClick={() => setShowPolicies(!showPolicies)}
                  className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 border border-gray-200 rounded-lg hover:bg-white transition"
                >
                  <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                    <BsShieldCheck size={18} />
                    Delivery & Returns
                  </span>
                  {showPolicies ? (
                    <HiChevronUp size={20} className="text-gray-600" />
                  ) : (
                    <HiChevronDown size={20} className="text-gray-600" />
                  )}
                </button>

                {showPolicies && (
                  <div className="mt-3 bg-white border border-gray-200 rounded-lg p-5 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                        <TbTruckDelivery className="text-white" size={18} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">
                          Free Delivery
                        </p>
                        <p className="text-xs text-gray-600">
                          On all orders above ₹999
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                        <MdOutlineKeyboardReturn
                          className="text-white"
                          size={18}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">
                          Easy Returns
                        </p>
                        <p className="text-xs text-gray-600">
                          7-day hassle-free return policy
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                        <HiOutlineVideoCamera
                          className="text-white"
                          size={18}
                        />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">
                          Unboxing Video Required
                        </p>
                        <p className="text-xs text-gray-600">
                          Record video for refund claims
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="hidden lg:block bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-4">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                      <TbTruckDelivery className="text-white" size={18} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">
                        Free Delivery
                      </p>
                      <p className="text-xs text-gray-600">
                        On all orders above ₹999
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                      <MdOutlineKeyboardReturn
                        className="text-white"
                        size={18}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">
                        Easy Returns
                      </p>
                      <p className="text-xs text-gray-600">
                        7-day hassle-free return policy
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                      <HiOutlineVideoCamera className="text-white" size={18} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">
                        Unboxing Video Required
                      </p>
                      <p className="text-xs text-gray-600">
                        Record video for refund claims
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-6 border-t border-gray-200">
                <div className="flex gap-1 text-xs text-gray-500 items-center">
                  <MdVerified size={16} className="text-green-600" />
                  <span>Secure checkout • 100% authentic products</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 lg:mt-24">
            <div className="border-b border-gray-200">
              <nav className="flex gap-8 overflow-x-auto scrollbar-hide">
                <button
                  onClick={() => setActiveTab("description")}
                  className={`pb-4 text-sm sm:text-base font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === "description"
                      ? "border-black text-black"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Description
                </button>
                <button
                  onClick={() => setActiveTab("reviews")}
                  className={`pb-4 text-sm sm:text-base font-medium whitespace-nowrap border-b-2 transition-colors ${
                    activeTab === "reviews"
                      ? "border-black text-black"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Reviews ({stats.total})
                </button>
              </nav>
            </div>

            <div className="mt-8">
              {activeTab === "description" && (
                <div className="max-w-3xl">
                  <p className="text-gray-700 leading-relaxed">
                    {product.description}
                  </p>
                </div>
              )}

              {activeTab === "reviews" && (
                <div className="space-y-8">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <h3 className="text-xl sm:text-2xl font-semibold text-gray-900">
                      Customer Reviews
                    </h3>
                    <button
                      onClick={() => setShowReviewForm(!showReviewForm)}
                      className="px-6 py-3 rounded-lg text-white text-sm font-medium tracking-wide hover:opacity-90 transition flex items-center justify-center gap-2"
                      style={{ background: PALETTE.ACCENT_BROWN }}
                    >
                      {showReviewForm ? "Cancel" : "Write a Review"}
                    </button>
                  </div>

                  {showReviewForm && (
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-3">
                          Rating <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center gap-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setRating(star)}
                              onMouseEnter={() => setHover(star)}
                              onMouseLeave={() => setHover(0)}
                              className="transition-transform hover:scale-110"
                            >
                              <FaStar
                                size={32}
                                className={`${
                                  star <= currentRating
                                    ? "text-yellow-400"
                                    : "text-gray-300"
                                }`}
                              />
                            </button>
                          ))}
                          {currentRating > 0 && (
                            <span
                              className={`ml-3 text-sm font-semibold ${currentDescription.color}`}
                            >
                              {currentDescription.text}
                            </span>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Review <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-black transition-colors resize-none"
                          rows="5"
                          placeholder="Share your thoughts about this product..."
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          maxLength={500}
                        />
                        <div className="flex justify-between items-center mt-2">
                          <p className="text-xs text-gray-500">
                            Minimum 10 characters
                          </p>
                          <p className="text-xs text-gray-500">
                            {comment.length}/500
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            setShowReviewForm(false);
                            setRating(0);
                            setHover(0);
                            setComment("");
                          }}
                          className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={submitReview}
                          disabled={
                            submitting || !rating || comment.trim().length < 10
                          }
                          className="flex-1 px-6 py-3 text-white rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                          style={{
                            background:
                              submitting ||
                              !rating ||
                              comment.trim().length < 10
                                ? "#9CA3AF"
                                : PALETTE.ACCENT_BROWN,
                          }}
                        >
                          {submitting ? "Submitting..." : "Submit Review"}
                        </button>
                      </div>
                    </div>
                  )}

                  {loadingReviews ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-pulse text-gray-400">
                        Loading reviews...
                      </div>
                    </div>
                  ) : reviews.length === 0 && !showReviewForm ? (
                    <div className="text-center py-12 bg-gray-50 rounded-xl">
                      <p className="text-gray-500 text-lg mb-4">
                        No reviews yet
                      </p>
                      <button
                        onClick={() => setShowReviewForm(true)}
                        className="text-sm font-medium underline"
                        style={{ color: PALETTE.ACCENT_BROWN }}
                      >
                        Be the first to review
                      </button>
                    </div>
                  ) : reviews.length > 0 ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 p-6 bg-gray-50 rounded-xl">
                        <div className="flex flex-col items-center justify-center">
                          <div className="text-5xl font-bold text-gray-900 mb-2">
                            {stats.average}
                          </div>
                          <div className="flex gap-1 mb-2">
                            {[...Array(5)].map((_, i) => (
                              <FaStar
                                key={i}
                                size={20}
                                className={
                                  i < Math.round(stats.average)
                                    ? "text-yellow-400"
                                    : "text-gray-300"
                                }
                              />
                            ))}
                          </div>
                          <p className="text-sm text-gray-600">
                            Based on {stats.total} reviews
                          </p>
                        </div>

                        <div className="md:col-span-2 space-y-2">
                          {[5, 4, 3, 2, 1].map((rating) => {
                            const count = stats.distribution[rating] || 0;
                            const percentage = stats.total
                              ? ((count / stats.total) * 100).toFixed(0)
                              : 0;

                            return (
                              <div
                                key={rating}
                                className="flex items-center gap-3 cursor-pointer hover:bg-white p-2 rounded transition"
                                onClick={() =>
                                  setReviewFilter(rating.toString())
                                }
                              >
                                <span className="text-sm font-medium w-12 flex items-center gap-1">
                                  {rating}
                                  <FaStar
                                    size={12}
                                    className="text-yellow-400"
                                  />
                                </span>
                                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full bg-black transition-all"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                                <span className="text-sm text-gray-600 w-12 text-right">
                                  {count}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <span className="text-sm text-gray-600">Filter:</span>
                        <select
                          value={reviewFilter}
                          onChange={(e) => setReviewFilter(e.target.value)}
                          className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-black transition"
                        >
                          <option value="all">All Ratings</option>
                          <option value="5">5 Stars</option>
                          <option value="4">4 Stars</option>
                          <option value="3">3 Stars</option>
                          <option value="2">2 Stars</option>
                          <option value="1">1 Star</option>
                        </select>
                      </div>

                      <div className="space-y-6">
                        {filteredReviews.length === 0 ? (
                          <p className="text-center text-gray-500 py-8">
                            No reviews match your filter
                          </p>
                        ) : (
                          filteredReviews.map((review) => (
                            <div
                              key={review._id}
                              className="border-b border-gray-200 pb-6 last:border-b-0"
                            >
                              <div className="flex justify-between items-start mb-3">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center text-white font-semibold">
                                    {review.user?.name
                                      ?.charAt(0)
                                      .toUpperCase() || "U"}
                                  </div>
                                  <div>
                                    <p className="font-semibold text-gray-900">
                                      {review.user?.name || "Anonymous"}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {new Date(
                                        review.createdAt,
                                      ).toLocaleDateString("en-IN", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                      })}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex gap-0.5">
                                  {[...Array(5)].map((_, i) => (
                                    <FaStar
                                      key={i}
                                      size={16}
                                      className={
                                        i < review.rating
                                          ? "text-yellow-400"
                                          : "text-gray-300"
                                      }
                                    />
                                  ))}
                                </div>
                              </div>

                              <p className="text-gray-700 leading-relaxed">
                                {review.comment}
                              </p>

                              <div className="mt-3">
                                <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-3 py-1 rounded-full">
                                  <MdVerified size={14} />
                                  Verified Purchase
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {relatedProducts.length > 0 && (
            <div className="mt-20">
              <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-8">
                You May Also Like
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                {relatedProducts.map((p) => (
                  <ProductCard
                    key={p._id}
                    product={p}
                    cart={cart}
                    setCart={setCart}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </Layout>
  );
};

export default ProductDetails;
